/**
 * Profile Page Component
 * 
 * User profile page with account information and subscription details.
 * Based on the profile.html mockup design.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks';
import { useTranslation } from '../../i18n/hooks';
import { SubscriptionStatusDisplay, useSubscriptionLoading, useSubscription } from '../../subscription';
import { PageContainer, ArbiWinHeader, ArbiWinFooter, MainContainer, Button } from '../../ui';
import { NotificationCenter } from '../../notifications';
import { ENV_CONFIG } from '../../../config/environment';
import './ProfilePage.css';

/**
 * Profile Page Component
 * @param {Object} props - Component props
 * @returns {JSX.Element} Profile page component
 */
const ProfilePage = ({ onHomeClick, onLogin, onRegister, onLogout }) => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { anyLoading } = useSubscriptionLoading();
  const { email } = useSubscription();
  const navigate = useNavigate();
  const showNotificationCenter = ENV_CONFIG.features.telegramNotifications;

  // The SubscriptionProvider will automatically load the status when the app starts
  // No need to manually trigger it here to avoid infinite loops

  const handleChangePassword = () => {
    navigate('/change-password');
  };


  return (
    <PageContainer>
      <ArbiWinHeader
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={onLogin}
        onRegister={onRegister}
        onLogout={onLogout}
        onHomeClick={onHomeClick}
      />

      <MainContainer>
        <div className="profile-page">
          <div className="profile-container">
            <h1 className="profile-title">{t('profile.title')}</h1>
            
            <div className="profile-card">
              {/* Account Information Section */}
              <div className="profile-section">
                <h2 className="profile-section-title">
                  {t('profile.sections.accountInfo')}
                </h2>
                <div className="profile-form">
                  <div className="form-group">
                    <label 
                      className="form-label" 
                      htmlFor="email"
                    >
                      {t('profile.fields.email')}
                    </label>
                    <input 
                      className="form-input" 
                      id="email" 
                      type="email" 
                      value={email || user?.email || ''}
                      disabled 
                    />
                  </div>
                </div>
              </div>

              {/* Security & Subscription Section */}
              <div className="profile-section profile-section--border">
                <h2 className="profile-section-title">
                  {t('profile.sections.securitySubscription')}
                </h2>
                <div className="profile-actions">
                  <button 
                    className="profile-action-button"
                    onClick={handleChangePassword}
                  >
                    <span className="profile-action-text">
                      {t('profile.actions.changePassword')}
                    </span>
                    <svg 
                      className="profile-action-icon" 
                      fill="currentColor" 
                      height="20px" 
                      viewBox="0 0 256 256" 
                      width="20px" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {showNotificationCenter && (
                <div className="profile-section profile-section--border">
                  <NotificationCenter />
                </div>
              )}

              {/* Subscription Status Display */}
              <div className="profile-section profile-section--border">
                <h2 className="profile-section-title">
                  {t('profile.sections.subscriptionStatus')}
                </h2>
                {anyLoading ? (
                  <div className="subscription-loading">
                    <div className="loading-spinner"></div>
                    <p>{t('subscription.loading.status')}</p>
                  </div>
                ) : (
                  <SubscriptionStatusDisplay 
                    showActions={true}
                    onActivate={() => navigate('/subscription')}
                    onChangePlan={() => console.log('Change plan')}
                    onCancel={() => console.log('Cancel subscription')}
                  />
                )}
              </div>

              {/* Back Section */}
              <div className="profile-section profile-section--border profile-section--back">
                <Button 
                  variant="secondary"
                  onClick={() => navigate('/dashboard')}
                  className="profile-back-button"
                >
                  {t('profile.actions.back')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </MainContainer>

      <ArbiWinFooter />
    </PageContainer>
  );
};

export default ProfilePage;
